import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Desktop, Package } from '@phosphor-icons/react';

import { BACKEND_URL, apiPath } from '../config/apiBase';
import { getUserClients, setAssetClient } from '../modules/assets/api';
const API = apiPath('/api');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

const STATUS_COLORS = {
  available: '#DCFCE7', in_use: '#DBEAFE', assigned: '#DBEAFE',
  maintenance: '#FEF3C7', in_repair: '#FEF3C7', retired: '#F3F4F6',
  lost: '#FEE2E2', damaged: '#FEE2E2',
};

const STATUS_ICON_COLORS = {
  available: '#16A34A', in_use: '#2563EB', assigned: '#2563EB',
  maintenance: '#B45309', in_repair: '#B45309', retired: '#6B7280',
  lost: '#DC2626', damaged: '#DC2626',
};

/**
 * List of assets currently assigned to a user. Used inside the Users detail
 * modal, the Profile page, and the Assets module's Member page. Read-only
 * everywhere by default; pass `editable` (only where the viewer is known to
 * have Assets module access) to let them tag each asset with which of the
 * user's clients it's for, without going through Inventory's Unassign/Reassign.
 *
 * `clientFilter` (optional): when viewing a member under a specific client in
 * Explorer, highlight assets tagged to that client — but still show every
 * assigned asset (Inventory assignments often have no assigned_client_id yet).
 */
export default function UserAssets({ userId, title = 'Assigned Assets', editable = false, clientFilter = null }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [savingAssetId, setSavingAssetId] = useState(null);
  const [onlyMatching, setOnlyMatching] = useState(false);

  const refresh = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/assets/user/${userId}`, authHeaders())
      .then((res) => setAssets(Array.isArray(res.data) ? res.data : []))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load assets'))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const matchesFilter = (a) => {
    if (!clientFilter) return true;
    return (a.assigned_client_id || a.client_id) === clientFilter;
  };

  const visibleAssets = (clientFilter && onlyMatching)
    ? assets.filter(matchesFilter)
    : assets;
  const matchingCount = clientFilter ? assets.filter(matchesFilter).length : assets.length;

  useEffect(() => {
    if (!editable || !userId) return;
    getUserClients(userId).then(setClientOptions).catch(() => setClientOptions([]));
  }, [editable, userId]);

  const saveClient = async (assetId, clientId) => {
    setSavingAssetId(assetId);
    try {
      await setAssetClient(assetId, clientId);
      setEditingAssetId(null);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update client');
    } finally {
      setSavingAssetId(null);
    }
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div style={{
          width: '30px', height: '30px', borderRadius: '8px', background: '#DBEAFE', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Desktop size={16} weight="bold" color="#2563EB" />
        </div>
        <span style={{ fontSize: '12px', fontWeight: '600' }}>{title}</span>
        {!loading && !error && (
          <span className="badge" style={{ fontSize: '11px', background: '#DBEAFE', color: '#1D4ED8', fontWeight: '600' }}>
            {visibleAssets.length}
          </span>
        )}
        {clientFilter && !loading && assets.length > 0 && (
          <button
            type="button"
            onClick={() => setOnlyMatching((v) => !v)}
            style={{
              marginLeft: 'auto', fontSize: '11px', background: onlyMatching ? '#EEF2FF' : 'transparent',
              border: '1px solid #E5E7EB', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', color: '#374151',
            }}
          >
            {onlyMatching
              ? `Showing tagged to this client (${matchingCount})`
              : `All assigned · ${matchingCount} tagged to this client`}
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ fontSize: '12px', color: '#6B7280' }}>Loading assets…</p>
      ) : error ? (
        <p style={{ fontSize: '12px', color: '#B91C1C' }}>{error}</p>
      ) : visibleAssets.length === 0 ? (
        <div className="page-enter" style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '14px',
          background: '#F9FAFB', border: '1px dashed #E5E7EB', borderRadius: '10px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', background: '#F3F4F6', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Package size={18} color="#9CA3AF" />
          </div>
          <p style={{ fontSize: '12px', color: '#6B7280' }}>
            {assets.length > 0 && onlyMatching
              ? 'No assets tagged to this client yet — turn off the filter to see all assigned assets.'
              : 'No company assets assigned to this user.'}
          </p>
        </div>
      ) : (
        <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {visibleAssets.map((a) => {
            const forThisClient = clientFilter && matchesFilter(a);
            return (
            <div key={a.asset_id} className="hover-card" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
              padding: '8px 10px', border: `1px solid ${forThisClient ? '#BFDBFE' : '#E5E7EB'}`,
              borderRadius: '8px', background: forThisClient ? '#F8FBFF' : 'white',
            }}>
              <div style={{
                width: '30px', height: '30px', borderRadius: '8px', flexShrink: 0,
                background: STATUS_COLORS[a.status] || '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Desktop size={16} weight="bold" color={STATUS_ICON_COLORS[a.status] || '#6B7280'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '12px', fontWeight: '600' }}>
                  {a.name}
                  {a.asset_tag && (
                    <span style={{ color: '#6B7280', fontWeight: '400', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {' '}· {a.asset_tag}
                    </span>
                  )}
                </p>
                <p style={{ fontSize: '12px', color: '#6B7280', textTransform: 'capitalize' }}>
                  {(a.asset_type || '').replace(/_/g, ' ')}
                  {a.serial_number && ` · SN ${a.serial_number}`}
                </p>
                {a.assigned_at && (
                  <p style={{ fontSize: '11px', color: '#9CA3AF' }}>
                    Assigned {new Date(a.assigned_at).toLocaleString(undefined, {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                )}
                {a.client_id ? (
                  a.client_name && (
                    <p style={{ fontSize: '11px', color: '#7C3AED', textTransform: 'none' }}>
                      Client: {a.client_name}
                    </p>
                  )
                ) : editable && clientOptions.length > 0 ? (
                  editingAssetId === a.asset_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <select
                        defaultValue={a.assigned_client_id || ''}
                        disabled={savingAssetId === a.asset_id}
                        onChange={(e) => saveClient(a.asset_id, e.target.value)}
                        className="input-field"
                        style={{ fontSize: '11px', padding: '2px 6px' }}
                      >
                        <option value="">No specific client</option>
                        {clientOptions.map((c) => (
                          <option key={c.client_id} value={c.client_id}>{c.name}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => setEditingAssetId(null)}
                        disabled={savingAssetId === a.asset_id}
                        style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0 }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setEditingAssetId(a.asset_id)}
                      style={{
                        fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: '2px',
                        color: a.assigned_client_name ? '#2563EB' : '#002FA7', fontWeight: a.assigned_client_name ? '500' : '600',
                      }}>
                      {a.assigned_client_name ? `Client: ${a.assigned_client_name}` : '+ Set client'}
                    </button>
                  )
                ) : (
                  a.assigned_client_name && (
                    <p style={{ fontSize: '11px', color: '#2563EB', textTransform: 'none' }}>
                      Client: {a.assigned_client_name}
                    </p>
                  )
                )}
              </div>
              <span className="badge" style={{
                background: STATUS_COLORS[a.status] || '#F3F4F6', textTransform: 'capitalize',
                whiteSpace: 'nowrap',
              }}>
                {(a.status || '').replace(/_/g, ' ')}
              </span>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
